/**
 * Crisp Hover Zoom — shared hover-enlargement for card surfaces OUTSIDE the
 * board slots (reveal floats, revealed cards in the enemy hand).
 *
 * Technique lifted from the battle-tested board-card hover in board.js
 * (deliberately NOT refactored out of board.js — that code is brittle and
 * proven; keep the two in sync if the technique ever changes):
 *  - Chrome/Firefox animate CSS `zoom` so the card re-rasterizes CRISP at the
 *    enlarged size (`transform: scale()` stays blurry until repaint).
 *  - Safari animates `--card-hover-transform` instead.
 *
 * Divergences from the board version, both hit-testing driven (like the
 * player-hand hover): the hover element is the CARD itself, whose zoomed box
 * IS the hit area, so the pointer can wander over the whole enlarged card
 * (e.g. down to the description) without collapsing it. The enlarged box is
 * clamped into the viewport with an animated vertical shift (cards that start
 * half off-screen, like the enemy hand row, slide fully into view). And
 * because that hit area MOVES while it is hovered, the engine decides from
 * the pointer, never from the browser's enter/leave alone — see the note on
 * `attach` below.
 */


import { Cards } from './cards.js';
import { CardDescEngraving } from './card-desc-engraving.js';
import { CardTilt } from './card-tilt.js';
import { Env } from './env.js';
import { TouchPreview } from './touch-preview.js';
import { uiPx } from './ui-scale.js';
import { usedBoxSize } from './used-box-size.js';
let CrispHover = {};

CrispHover = (function () {
    // Hover-engine branch (historic name: `_isSafari`). True means "use the
    // transform fallback" — legacy Safari's zoom didn't scale abs-positioned
    // children. Env's runtime capability probe now decides (modern WebKit
    // graduates to crisp zoom); the UA fork is a load-order-hiccup fallback.
    const _isSafari = Env
        ? !Env.useZoomHover
        : (/Safari/i.test(navigator.userAgent) && !/Chrome/i.test(navigator.userAgent));
    const DEFAULT_MAX_SCALE = 2.4;

    // The description keeps its RESTING lines for as long as the card is
    // enlarged: engraved when a grow leaves rest, released only by clearZoom
    // (back at rest) or when the card leaves the document. Per-frame zoom
    // otherwise tips a word across a wrap point, and releasing at full size —
    // the July version, kept in step with board.js — re-wrapped the fully
    // grown card as it settled (Ember Artisan, 2026-09-24). The engraving is
    // shared with every enlargement: card-desc-engraving.js (the same
    // paragraph with its breaks forced; the text restored byte-for-byte).
    function pinDesc(cardElement) {
        return CardDescEngraving.engrave(cardElement);
    }
    function unpinDesc(cardElement) {
        CardDescEngraving.release(cardElement);
    }

    /* THE POINTER IS THE TRUTH, NOT THE BOX.
       The hover element here is the card itself, and the card MOVES while it is
       hovered: the zoom grows its box, the slide brings it on screen, the fan's
       own transform un-rotates it and CardTilt leans it (the flex row used to
       re-centre around the new width as well; the footprint is held now, see
       below). Every one of those can push the box out from under
       a pointer that never moved, and the browser reports that as `mouseleave`
       — the card shrank, the box slid back under the pointer, `mouseenter`,
       grow again: the enlarge/shrink fight the player saw on a revealed enemy
       card ("it looks like it's fighting to grow"). Deferring the shrink by a
       frame was not enough, because in the SETTLED state the pointer really
       was outside the box: the card landed lower than computed (the rest box
       is measured rotated, the hover translate was multiplied by the zoom) and
       the one-shot correction only ran after an uninterrupted grow.
       So the engine no longer believes the box. It remembers where the pointer
       is, treats a leave whose pointer has not moved as the card's own motion,
       aims the slide so the pointer stays its margin inside the box (re-aimed,
       continuously, for as long as the pointer is still arriving), and decides
       whether the pointer is still on the card in two regimes:
         - ARRIVING (a grow in flight, or the settle after it, kept open for
           ARRIVAL_MS after every pointer move): per frame, against the box the
           card is heading for — an event that lands mid-frame sees a box that
           is about to change;
         - AT REST: per event, against the box the card occupies. The card
           never chases a pointer down or across itself; a pointer climbing
           past its top edge tightens the slide instead of releasing it, so a
           card whose rest box reaches above its held box cannot shrink and
           re-grow under a pointer that only pushed upward.
       AND THE MOTION IS ONE CURVE. The revealed card's grow was painted as
       three or four steps ("3 discrete movements"): the card art was
       re-decoded on every frame (fixed in cards.js — layout jitter is not a
       resize), the slide was aimed at the ROTATED rest box and snapped 12 px
       when a one-shot correction ran at the end, the fan's un-rotation eased
       on its own CSS clock (which Chrome restarts on every zoom write), and
       CardTilt's lean stayed aimed at the small card until the next physical
       nudge. Now one frame loop drives everything on one ease: it writes the
       fan's pose and aims the lean (at the box the card is heading to, blended
       in with the grow and out with the shrink), MEASURES the box with those
       in it and folds the difference between measured and written — the
       residual: the pose's own geometry, the lean, row realignment — into the
       top it writes, so the box follows the eased path exactly and needs no
       correction step.
       Contract: tests/unit/ui/crispHover.test.js ("pointer truth", "one
       motion") and tests/e2e-playwright/enemy-reveal-hover.spec.js. */
    /* How far inside the box's top the slide keeps the pointer. The lean's
       perspective skews the projected top edge by up to ~5 px against the
       bounding box (the far corner sits lower than the near one), so a margin
       that only cleared an integer event coordinate left a sliver pointer on
       the edge itself. */
    const CONTAIN_MARGIN_PX = 12;
    // Same allowance as board.js's SLOT_EDGE_TOLERANCE_PX: an integer event
    // coordinate against a fractional box edge.
    const EDGE_TOLERANCE_PX = 1;

    /* ONE MOTION, ONE CLOCK.
       The grow and the shrink are eased here, per frame, and so is the fan's
       pose (below). The surface's own transform transition — which still
       eases the lean after the pointer while the card is held, and stands in
       for the pose when the engine cannot parse it — reads these values back
       (game-board.css, locked by tests/unit/styles/enemyHandHoverMotion.test.js)
       so that nothing on the card ever runs on a second curve or clock. */
    const GROW_MS = 200;
    const SHRINK_MS = 150;
    /* The shrink of a card handing its enlargement to a neighbour. Quicker
       than a plain release so that, at any frame cadence, the outgoing card
       is small before the incoming one is big: one card at a time, with the
       eye on the one arriving. */
    const HANDOFF_SHRINK_MS = 100;
    const MOTION_EASE_CSS = 'cubic-bezier(0.33, 1, 0.68, 1)'; // ease-out cubic
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    /* THE FAN'S POSE, ON THE ENGINE'S CLOCK.
       The revealed card stands up as it grows. That used to be a CSS
       transition on `transform` — and Chrome restarts a transform transition
       on EVERY frame the element's zoom changes (computed transform lengths
       are stored zoomed), so the pose could only ever trail the zoom on a
       restarted exponential: the card finished growing, then finished
       straightening (measured 2026-09-18: 10° → 0.5° at the zoom's end and
       ~150 ms of tail; `top` writes do not restart it, zoom writes do). So
       the engine writes the pose itself, per frame, from the fan's resting
       `--card-base-transform` with every function argument multiplied by the
       pose fraction (rotate(10deg) → rotate(calc((10deg) * 0.3700))) — a
       straight path from the fan's pose to upright on the same ease as the
       size, with no CSS interpolation to lag. `crisp-moving` is held while a
       path runs so the surface rule can drop its transform transition for
       exactly that long. A resting value the parser does not understand
       leaves the pose to the surface rule and its transition, as before. */
    const POSE_SCALED = new Set(['rotate', 'rotatez', 'translate', 'translatex', 'translatey', 'skew', 'skewx', 'skewy']);
    const POSE_IDENTITY_SCALE = new Set(['scale', 'scalex', 'scaley']);
    function parsePose(value) {
        const text = String(value || '').trim();
        if (!text || text === 'none') return { functions: [] };
        const functions = [];
        let i = 0;
        while (i < text.length) {
            if (/\s/.test(text[i])) { i += 1; continue; }
            const open = text.indexOf('(', i);
            if (open === -1) return null;
            const name = text.slice(i, open).trim();
            if (!/^[a-zA-Z0-9]+$/.test(name)) return null;
            let depth = 1;
            let j = open + 1;
            while (j < text.length && depth > 0) {
                if (text[j] === '(') depth += 1;
                else if (text[j] === ')') depth -= 1;
                j += 1;
            }
            if (depth !== 0) return null;
            const inner = text.slice(open + 1, j - 1);
            const args = [];
            let d = 0;
            let start = 0;
            for (let k = 0; k < inner.length; k += 1) {
                const c = inner[k];
                if (c === '(') d += 1;
                else if (c === ')') d -= 1;
                else if (c === ',' && d === 0) { args.push(inner.slice(start, k).trim()); start = k + 1; }
            }
            args.push(inner.slice(start).trim());
            const fn = name.toLowerCase();
            if (POSE_SCALED.has(fn) && args.every((a) => a.length > 0)) {
                functions.push({ name, args });
            } else if (POSE_IDENTITY_SCALE.has(fn) && args.every((a) => parseFloat(a) === 1)) {
                // scale(1) is the identity: nothing to scale away.
            } else {
                return null;
            }
            i = j;
        }
        return { functions };
    }
    /** The pose `fraction` of the way from upright (0) to the fan's rest (1). */
    function poseAt(pose, fraction) {
        if (!pose || !pose.functions.length) return 'scale(1)';
        const p = Math.max(0, Math.min(1, fraction)).toFixed(4);
        return pose.functions
            .map(({ name, args }) => `${name}(${args.map((a) => `calc((${a}) * ${p})`).join(', ')})`)
            .join(' ');
    }
    /* After a path ends — and after any pointer move while the card is held,
       which re-eases the lean — the box keeps settling on its own for as long
       as the transform transition is still easing; the loop keeps the intended
       top true until it has stood still. */
    const SETTLE_WATCH_MS = 500;
    /* THE SLIDE FOLLOWS A POINTER THAT IS STILL ARRIVING. A pointer entering
       through the bottom and climbing to the top sliver is still travelling
       while the card grows (a real mouse, and Playwright WebKit's slow steps),
       so where the slide must end changes mid-flight — and can change after
       the grow has ended. Moving the path's end point stepped the card back
       by (change × progress); re-basing the slide from where the card is,
       over at least this long, keeps it continuous. */
    const REAIM_MS = 120;
    /* How long after the last pointer move (or the path's end) the card still
       counts as ARRIVING — re-aiming freely and judged against the box it is
       heading for. Longer than the gap between two of WebKit's stepped moves,
       so a slowly entering pointer never falls out of the moving regime
       between two steps. */
    const ARRIVAL_MS = 150;

    /* ONE AT A TIME, THE FRIENDLY HAND'S WAY. Every attached card's engine, so
       a card can hand its enlargement to the neighbour the pointer crossed
       into — which sits UNDER the enlarged card and never hears an enter of
       its own from the browser. */
    const engines = new WeakMap();

    function eventPoint(event) {
        return event && Number.isFinite(event.clientX) && Number.isFinite(event.clientY)
            ? { x: event.clientX, y: event.clientY }
            : null;
    }

    /**
     * @param {HTMLElement} hoverElement element that owns enter/leave (usually the card itself)
     * @param {HTMLElement} cardElement  the .card to zoom
     * @param {Object} [opts]
     *   - maxScale: zoom ceiling (default 2.4 — a comfortable reading size)
     *   - fitMargin: min px between the enlarged card and the viewport edges (default 12)
     *   - zoomClass: class held on cardElement while zoomed (CSS kills filters
     *     under it, since ancestor filters soften zoomed content) (default 'crisp-zooming')
     *   - getBlockReason: () => string|null — abort hover while truthy
     *
     * Two classes are written. `zoomClass` is held for as long as the card is
     * bigger than rest, shrink included. `crisp-hovered` is held only while the
     * pointer holds the card — added when the grow begins, removed the moment
     * the shrink does — and is the hook for surface CSS that must change with
     * the HOVER rather than with `:hover` (the enemy fan's upright rule): a
     * `:hover` rule flips the instant the card moves out from under the
     * pointer, which is exactly the motion it caused.
     */
    function attach(hoverElement, cardElement, opts = {}) {
        const maxScale = typeof opts.maxScale === 'number' ? opts.maxScale : DEFAULT_MAX_SCALE;
        const fitMargin = uiPx(typeof opts.fitMargin === 'number' ? opts.fitMargin : 12);
        const zoomClass = opts.zoomClass || 'crisp-zooming';
        const getBlockReason = typeof opts.getBlockReason === 'function' ? opts.getBlockReason : () => null;
        /* Opt-in sharp enlargement. Scaling the unit re-lays-out the card so its
           art rasters at the displayed size, but it also changes geometry for
           every consumer — board cards included, whose landing/hover motion is
           pinned by game-juice specs. Only the surfaces that reported blur ask
           for it. */
        const sharpZoom = opts.sharpZoom === true;

        let restRect = null;      // the card's resting box (screen px)
        /* THE OTHER CARDS NEVER MOVE, AND NEITHER DOES THE BOARD. The zoom
           (and the unit path) grow the card's LAYOUT box in both axes.
           Across: the fan is a centred flex row, and every sibling used to be
           re-spaced by up to 70 px around the card being inspected.
           Down: the fan and the enemy board share one flex column
           (#opponent-area) that overflows on most desktop page boxes, so the
           fan is shrunk to its CONTENT-based minimum height — and the zoomed
           card was that content. The fan stopped shrinking and pushed the
           board, slots and summoned cards, 2-5 px down for as long as a
           revealed card was enlarged (owner, 2026-09-28, MacBook Air).
           The friendly hand holds the card's resting footprint in both axes
           (cards.js applyHoverTransform: zoom plus margin compensation), so
           this engine does the same — the resting margin box, measured once,
           is what the row keeps seeing at every zoom. The side margins share
           the width's growth, so the card's centre holds; the top margin keeps
           the top edge where it was and the bottom margin takes the height's
           growth. Inline margins render zoomed on the engines Env has probed
           (`zoomScalesMargins`), in screen pixels otherwise and on the unit
           path. The transform path grows nothing. */
        let footprint = null;
        /* The card's RESTING shape — the slot the friendly hand judges hovers
           by — captured before the hold changes anything: the layout box under
           its rest transform, inverted per point. Where the platform has no
           DOMMatrix it is the resting bounding box. */
        let restShape = null;
        /* The card's paint order at rest (its computed z-index before the
           hold raises it): in the band where two resting cards overlap, the
           one painted on top owns the pointer — the browser's own rest hit
           test, restated for a card that is currently enlarged. */
        let restZ = 0;
        /* Shrinking because a neighbour took the pointer: a browser enter
           on this still-large box is stale (the pointer is provably in the
           neighbour's slot) and must not restart the hold; the neighbour
           hands back once this card is at rest if the pointer is here. */
        let handedOff = false;
        let zoomed = false;
        let hoverGeneration = 0;
        let pointerInside = false;
        /* Where the pointer was last KNOWN to be over the card, in screen px.
           Null for a hover with no pointer (touch inspection, a synthetic enter
           without coordinates), which keeps the classic unconditional
           enter/leave semantics. */
        let pointer = null;
        /* The box the running grow is heading for: its scale and analytic top.
           Set from the first grow frame until the box has settled; null at rest
           and once a shrink begins. */
        let destination = null;
        /* THE SCREEN GEOMETRY THE ENGINE INTENDS FOR THIS FRAME: the scale, the
           top of the box, the horizontal offset from the card's natural
           position, and the pose (1 = the fan's rest, 0 = upright). The path is
           evaluated into it; the shrink starts from it. `top` is the screen top
           of the card's box; `left` is an offset. */
        let placed = null;
        /* The fan's resting pose, parsed for the engine to scale per frame;
           null when it is not the engine's to drive. */
        let pose = null;
        /* Where the lean is aimed: the box the card is heading to, and the
           pointer it was last aimed at — kept through the shrink so the lean
           can fade out on the same clock. */
        let heldBox = null;
        let lastPointer = null;
        /* What the last frame actually WROTE, in the same screen terms, and the
           residual between that and what the browser then measured — the part
           of the box's motion that is not the engine's (a rotated card's
           bounding box is not its layout box, the fan's un-rotation and
           CardTilt's lean move it as they ease, the row realigns around the
           zoomed card). Every frame writes `placed − residual`, so the measured
           box lands on the intended path with no correction step at the end.
           `write` puts `top`/`left` in three different spaces (the zoom and
           unit paths divide by the scale because their box grows, the transform
           path does not), so the value is remembered rather than re-derived. */
        let written = null;
        let residualTop = 0;
        let stillFrames = 0;
        let settleStart = 0;
        let arrivedAt = 0;
        /* The running path of the size and pose: from/to in `placed` terms,
           its clock, and what to do when it ends. Null while holding or at
           rest. The slide (the top) has its own path, `slide`, because it is
           re-aimed while the pointer is still arriving; it starts with the
           motion and is re-based on its own clock when the aim changes. */
        let motion = null;
        let slide = null;
        let fitTop = 0;
        let frame = 0;            // the engine's animation-frame handle
        let leaveFrame = 0;

        /* The ambient card unit, captured once before it is multiplied — a custom
           property cannot reference itself. */
        let ambientUnit = null;
        function readAmbientUnit() {
            if (ambientUnit) return ambientUnit;
            const raw = getComputedStyle(cardElement).getPropertyValue('--card-unit').trim();
            ambientUnit = raw || '1vmin';
            return ambientUnit;
        }

        function holdFootprint(z, marginsRenderZoomed) {
            if (!footprint) return;
            const { width, outer } = footprint;
            const margin = marginsRenderZoomed
                ? (outer / z - width) / 2
                : (outer - z * width) / 2;
            cardElement.style.marginLeft = `${margin}px`;
            cardElement.style.marginRight = `${margin}px`;
            if (!footprint.height) return;
            const { height, outerHeight, marginTop } = footprint;
            const top = marginsRenderZoomed ? marginTop / z : marginTop;
            const bottom = marginsRenderZoomed
                ? (outerHeight - marginTop) / z - height
                : outerHeight - marginTop - z * height;
            cardElement.style.marginTop = `${top}px`;
            cardElement.style.marginBottom = `${bottom}px`;
        }

        function write(z, screenTop, screenLeft = 0) {
            written = { z, top: screenTop, left: screenLeft };
            if (_isSafari && sharpZoom) {
                /* Scale the UNIT, never a transform. A transform is a compositor
                   operation: the layer rasterises at the element's layout box and
                   is then stretched, so the art can never be sharper than that
                   small box — which is exactly the "enemy revealed cards are
                   blurry" report, and the same defect `spell-announce.css`
                   already fixed for played spells by scaling the unit. Scaling
                   the unit is dimensionally identical and re-lays-out, so the
                   card paints at the size it is shown. */
                const unit = readAmbientUnit();
                cardElement.style.setProperty('--card-unit', `calc(${unit} * ${z})`);
                cardElement.style.setProperty('--card-width', `calc(12.29 * var(--card-unit))`);
                cardElement.style.setProperty('--card-height', `calc(22.2 * var(--card-unit))`);
                // The box now grows by layout, so position it like the zoom path.
                cardElement.style.top = `${(screenTop - restRect.top) / z}px`;
                cardElement.style.left = `${screenLeft / z}px`;
                holdFootprint(z, false);
            } else if (_isSafari) {
                cardElement.style.setProperty('--card-hover-transform', `scale(${z})`);
                cardElement.style.top = `${screenTop - restRect.top}px`;
                cardElement.style.left = `${screenLeft}px`;
            } else {
                cardElement.style.zoom = z;
                // `top`/`left` on a zoomed element render multiplied by the zoom.
                cardElement.style.top = `${(screenTop - restRect.top) / z}px`;
                cardElement.style.left = `${screenLeft / z}px`;
                holdFootprint(z, Env?.zoomScalesMargins !== false);
            }
        }

        function measureRestShape(r) {
            const box = { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
            const width = Number(cardElement.offsetWidth) || 0;
            const height = Number(cardElement.offsetHeight) || 0;
            if (!(width > 0) || !(height > 0) || typeof DOMMatrix !== 'function' || typeof DOMPoint !== 'function') {
                return { box, inverse: null };
            }
            try {
                const cs = getComputedStyle(cardElement);
                const transform = cs.transform && cs.transform !== 'none' ? cs.transform : '';
                const m = new DOMMatrix(transform);
                const origin = String(cs.transformOrigin || '').split(/\s+/).map(parseFloat);
                const ox = Number.isFinite(origin[0]) ? origin[0] : width / 2;
                const oy = Number.isFinite(origin[1]) ? origin[1] : 0;
                // Where the layout box's top-left sits on screen: the bounding
                // box's corner minus the smallest transformed corner offset.
                let minX = Infinity;
                let minY = Infinity;
                for (const [x, y] of [[0, 0], [width, 0], [0, height], [width, height]]) {
                    const p = m.transformPoint(new DOMPoint(x - ox, y - oy));
                    // A resting card has no lean: its corners stay in the plane.
                    if (Math.abs((p.w || 1) - 1) > 0.01) return { box, inverse: null };
                    minX = Math.min(minX, p.x + ox);
                    minY = Math.min(minY, p.y + oy);
                }
                return {
                    box, width, height, ox, oy,
                    layoutLeft: r.left - minX,
                    layoutTop: r.top - minY,
                    inverse: m.inverse(),
                };
            } catch (_) {
                return { box, inverse: null };
            }
        }

        function inRestShape(point) {
            if (!restShape) return false;
            const b = restShape.box;
            if (!restShape.inverse) {
                return point.x >= b.left && point.x <= b.right && point.y >= b.top && point.y <= b.bottom;
            }
            const { inverse, layoutLeft, layoutTop, ox, oy, width, height } = restShape;
            const local = inverse.transformPoint(new DOMPoint(point.x - layoutLeft - ox, point.y - layoutTop - oy));
            const x = local.x + ox;
            const y = local.y + oy;
            return x >= 0 && x <= width && y >= 0 && y <= height;
        }

        function zIndexOf(el) {
            const z = parseInt(getComputedStyle(el).zIndex, 10);
            return Number.isFinite(z) ? z : 0;
        }

        /* The card whose RESTING slot owns this point, by the browser's own
           hit test through the enlarged card: the first other card of the
           same fan in paint order (their rest z-order), skipping one that is
           itself still zooming — a card shrinking away is nobody's slot until
           it is back at rest, or two neighbours would trade the pointer back
           and forth across their overlap for as long as the shrink lasts. */
        function restOwnerAt(point) {
            if (typeof document.elementsFromPoint !== 'function') return null;
            const fan = cardElement.parentElement;
            if (!fan) return null;
            let stack;
            try { stack = document.elementsFromPoint(point.x, point.y) || []; } catch (_) { return null; }
            for (const el of stack) {
                const card = typeof el.closest === 'function' ? el.closest('.card') : null;
                if (!card || card === cardElement || card.parentElement !== fan) continue;
                if (card.classList.contains(zoomClass)) continue;
                return card;
            }
            return null;
        }

        /* Hand the enlargement to the neighbour the pointer crossed into: this
           card lets go NOW (a deliberate crossing, not an edge graze — the
           one-frame grace below would hold two cards for a frame) and the
           neighbour's engine takes the pointer. */
        function handOff(owner, point) {
            const engine = engines.get(owner);
            realLeave({ immediate: true, handoff: true });
            if (engine) engine.enter(point);
        }

        /* Does the pointer, over this held card, belong to a neighbour? Yes
           when it is outside this card's own resting shape and over another
           resting card, or inside this card's resting shape but over a card
           that is painted ABOVE it at rest (the overlap band belongs to the
           card on top, exactly as the browser decides it when nothing is
           enlarged). */
        function neighbourOwning(point) {
            const owner = restOwnerAt(point);
            if (!owner) return null;
            if (!inRestShape(point)) return owner;
            return zIndexOf(owner) > restZ ? owner : null;
        }

        /* ONE HOLDER PER FAN. Whatever path started this hold — the browser's
           enter on an exposed card, a neighbour's handoff, a touch — any
           other card of the fan still held lets go now. The pointer is on
           THIS card; two enlarged cards were the owner's "more than one at a
           time", and their overlapping boxes made the browser's hover flip
           between them: the flicker. */
        function releaseOtherHolders() {
            const fan = cardElement.parentElement;
            if (!fan || typeof fan.querySelectorAll !== 'function') return;
            for (const other of fan.querySelectorAll('.crisp-hovered')) {
                if (other === cardElement) continue;
                const engine = engines.get(other);
                if (engine) engine.release();
                else other.classList.remove('crisp-hovered');
            }
        }

        /* The card's resting margin box in the row, measured before anything
           is written: what the row must keep seeing at every zoom. The used
           size, fractional — whole pixels would come back multiplied by the
           zoom (used-box-size.js). */
        function measureFootprint() {
            const cs = getComputedStyle(cardElement);
            const { width, height } = usedBoxSize(cardElement, cs);
            if (!(width > 0)) return null;
            const marginLeft = parseFloat(cs.marginLeft) || 0;
            const marginRight = parseFloat(cs.marginRight) || 0;
            const marginTop = parseFloat(cs.marginTop) || 0;
            const marginBottom = parseFloat(cs.marginBottom) || 0;
            return {
                width,
                outer: width + marginLeft + marginRight,
                height,
                marginTop,
                outerHeight: height + marginTop + marginBottom,
                inlineLeft: cardElement.style.marginLeft,
                inlineRight: cardElement.style.marginRight,
                inlineTop: cardElement.style.marginTop,
                inlineBottom: cardElement.style.marginBottom,
            };
        }

        function measure() {
            const r = cardElement.getBoundingClientRect();
            return r.width > 0 ? r : null;
        }

        function stopLoop() {
            if (frame) { cancelAnimationFrame(frame); frame = 0; }
            motion = null;
            slide = null;
        }

        function clearZoom() {
            unpinDesc(cardElement);
            stopLoop();
            if (_isSafari) {
                cardElement.style.removeProperty('--card-unit');
                cardElement.style.removeProperty('--card-width');
                cardElement.style.removeProperty('--card-height');
                cardElement.style.removeProperty('--card-hover-transform');
            } else {
                cardElement.style.zoom = '';
            }
            cardElement.style.top = '';
            cardElement.style.left = '';
            cardElement.style.position = '';
            if (footprint) {
                cardElement.style.marginLeft = footprint.inlineLeft;
                cardElement.style.marginRight = footprint.inlineRight;
                cardElement.style.marginTop = footprint.inlineTop;
                cardElement.style.marginBottom = footprint.inlineBottom;
                footprint = null;
            }
            cardElement.classList.remove(zoomClass);
            cardElement.classList.remove('crisp-hovered');
            cardElement.classList.remove('crisp-moving');
            cardElement.style.removeProperty('--card-base-transform');
            delete cardElement.dataset.cardRasterEnlarging;
            CardTilt?.aim?.(cardElement, null);
            zoomed = false;
            handedOff = false;
            restRect = null;
            restShape = null;
            placed = null;
            pose = null;
            heldBox = null;
            lastPointer = null;
            written = null;
            residualTop = 0;
            destination = null;
        }

        /** Is the card in motion under the engine (grow in flight, or settling after it)? */
        function moving() {
            return Boolean(destination);
        }

        /** Is this screen point over the card's current box? */
        function isOverCard(point, slack = EDGE_TOLERANCE_PX) {
            const r = cardElement.getBoundingClientRect();
            return r.width > 0
                && point.x >= r.left - slack
                && point.x <= r.right + slack
                && point.y >= r.top - slack
                && point.y <= r.bottom + slack;
        }

        /**
         * Can the running grow still end with this point inside the card?
         *
         * The box it is heading for: the rest box scaled about its own centre
         * (the card's footprint in the row is held, so its centre holds and
         * its neighbours stay put), its top wherever the slide will put it.
         * Judged with the containment margin.
         */
        function canReach(point) {
            if (!destination || !restRect) return false;
            const margin = uiPx(CONTAIN_MARGIN_PX);
            const halfWidth = (restRect.width * destination.z) / 2;
            const top = Math.min(fitTop, point.y - margin);
            const bottom = top + restRect.height * destination.z;
            return point.x >= restRect.cx - halfWidth - margin
                && point.x <= restRect.cx + halfWidth + margin
                && point.y >= top - margin
                && point.y <= bottom + margin;
        }

        /* The top the slide aims for: the fit, unless that would move the box
           out from under the pointer (entering the topmost sliver of a half
           off-screen card would otherwise un-hover it). Re-read every frame
           the card is arriving, because the pointer may still be travelling
           into it. The margin holds even above the viewport edge: a pointer
           4 px into the sliver used to get a card whose top was clamped to 0,
           and the lean's perspective then swung that edge past the pointer —
           the browser's hover flapped, CardTilt snapped the lean to neutral
           and back, and the glow flickered. A few pixels of frame off screen
           are nothing next to that. */
        function aimTop() {
            if (!pointer) return fitTop;
            return Math.min(fitTop, pointer.y - uiPx(CONTAIN_MARGIN_PX));
        }

        /* Point the slide at `top`: continuous from where the card is, over
           what is left of the current slide or REAIM_MS, whichever is longer. */
        function aimSlideAt(top, now) {
            const remaining = slide ? slide.start + slide.duration - now : 0;
            slide = {
                from: placed.top,
                to: top,
                start: now,
                duration: Math.max(remaining, REAIM_MS),
            };
            if (destination) destination.top = top;
        }

        /* The slide's value at `now`, ending the slide when it is done. */
        function slideTop(now) {
            if (!slide) return placed.top;
            const t = Math.max(0, Math.min((now - slide.start) / slide.duration, 1));
            const top = slide.from + (slide.to - slide.from) * easeOutCubic(t);
            if (t >= 1) slide = null;
            return top;
        }

        /* The box the grow is heading to, for the lean: the rest box scaled
           about its centre, its top where the slide will put it. */
        function destinationBox() {
            if (!destination || !restRect) return null;
            const width = restRect.width * destination.z;
            return {
                left: restRect.cx - width / 2,
                top: destination.top,
                width,
                height: restRect.height * destination.z,
            };
        }

        function tick(now) {
            frame = 0;
            // Card removed / re-rendered mid-motion: stop and unwind so no pinned
            // wrapper leaks on a detached node.
            if (!cardElement.isConnected) {
                unpinDesc(cardElement);
                untrack();
                stopLoop();
                return;
            }
            if (getBlockReason()) {
                clearZoom();
                return;
            }
            // 1. Where the paths put the card this frame. While the card is
            //    arriving (its grow, then its settle) the slide keeps aiming at
            //    the pointer — re-based, never re-pointed, so the top is
            //    continuous. Once the grow has ended it only ever tightens: a
            //    pointer that then wanders down the card is not chased.
            let done = null;
            if (pointer && zoomed) {
                const want = aimTop();
                const current = slide ? slide.to : placed.top;
                if (Math.abs(want - current) > 0.5 && (motion || want < current)) aimSlideAt(want, now);
            }
            if (motion) {
                // Clamp below as well: the first rAF timestamp can PRECEDE the
                // performance.now() captured at the path's start, and a negative
                // t makes the ease overshoot negative — one frame of zoom < 1
                // shrinks the card, kicks the pointer out through the bottom
                // edge and fires a spurious mouseleave.
                const t = Math.max(0, Math.min((now - motion.start) / motion.duration, 1));
                const ease = easeOutCubic(t);
                placed = {
                    z: motion.from.z + (motion.to.z - motion.from.z) * ease,
                    top: placed.top,
                    left: motion.from.left + (motion.to.left - motion.from.left) * ease,
                    pose: motion.from.pose + (motion.to.pose - motion.from.pose) * ease,
                };
                if (t >= 1) {
                    done = motion.done;
                    motion = null;
                }
            }
            placed.top = slideTop(now);
            // 2. The pose and the lean ride the same fraction as the size. The
            //    lean is aimed where the card is going: it moved under the
            //    pointer, and the browser sends no pointermove for an element's
            //    own motion; naming the destination box keeps one target, and
            //    the blend eases the lean in with the grow and out with the
            //    shrink instead of snapping it. Both are transforms: written
            //    first, they are in the box the next step measures.
            if (pose) cardElement.style.setProperty('--card-base-transform', poseAt(pose, placed.pose));
            if (destination) heldBox = destinationBox();
            if (lastPointer && heldBox) CardTilt?.aim?.(cardElement, lastPointer, heldBox, 1 - placed.pose);
            // 3. What the box does on its own: the residual between what the
            //    last frame wrote and where the box is now, with THIS frame's
            //    pose and lean already in it (a transform change costs a style
            //    recalc, not a layout), so folding it in lands the box exactly
            //    where the path says — no correction step at the end.
            if (written) {
                const r = measure();
                if (r) {
                    const next = r.top - written.top;
                    stillFrames = Math.abs(next - residualTop) < 0.05 ? stillFrames + 1 : 0;
                    residualTop = next;
                }
            }
            // 4. Write the frame: the path, minus what the box does on its own.
            write(placed.z, placed.top - residualTop, placed.left);
            if (done) {
                // A finished GROW keeps the resting lines (releasing them at
                // full size re-wrapped the grown card). The shrink's `done` is
                // clearZoom, which releases them back at rest.
                done();
                if (!zoomed) return;
            }
            // `crisp-moving` (no transform transition) is held for as long as
            // the card is ARRIVING, not just while its path runs: a pointer
            // still stepping in re-aims the lean, and a transition on that
            // moved the box a frame ahead of the residual loop (a 2–3 px
            // wobble under load). Once the pointer has stopped, the surface's
            // transition returns for the hold.
            if (!motion && !slide && now - arrivedAt >= ARRIVAL_MS) cardElement.classList.remove('crisp-moving');
            // 5. The frame's verdict: a pointer this grow can no longer end up
            //    under has left.
            if (destination && pointer && !isOverCard(pointer) && !canReach(pointer)) {
                realLeave();
                return;
            }
            // 6. Next frame: while a path runs, then while the box is still
            //    settling on its own (after the grow, or after a pointer move
            //    re-eased the lean while the card was held).
            if (motion || slide) {
                frame = requestAnimationFrame(tick);
                return;
            }
            const arrived = now - arrivedAt >= ARRIVAL_MS;
            if ((stillFrames >= 2 && arrived) || now - settleStart > SETTLE_WATCH_MS) {
                destination = null;
                return;
            }
            frame = requestAnimationFrame(tick);
        }

        /* Keep the intended box true while the card's own transforms ease:
           the residual loop runs for a settle window, or until the box has
           stood still for two frames and the pointer has stopped arriving.
           At rest this never chases a pointer down the card; it holds the top
           against the lean that follows the pointer, and tightens the slide
           for a pointer climbing toward the top edge. */
        function watchSettle() {
            settleStart = performance.now();
            arrivedAt = settleStart;
            stillFrames = 0;
            if (!frame) frame = requestAnimationFrame(tick);
        }

        /* A pointer above the box's top edge but still over its width, inside
           the window: the slide can tighten to cover it (and does, next
           frame). That is the pointer pressing on the card's top, not leaving. */
        function climbsAbove(point) {
            const r = cardElement.getBoundingClientRect();
            return r.width > 0
                && point.y >= 0
                && point.y < r.top
                && point.x >= r.left - EDGE_TOLERANCE_PX
                && point.x <= r.right + EDGE_TOLERANCE_PX;
        }

        function beginHover() {
            if (!zoomed) {
                const r = measure();
                if (!r) return;
                restRect = { top: r.top, height: r.height, width: r.width, cx: r.left + r.width / 2 };
                placed = { z: 1, top: restRect.top, left: 0, pose: 1 };
                pose = parsePose(getComputedStyle(cardElement).getPropertyValue('--card-base-transform'));
                footprint = measureFootprint();
                restShape = measureRestShape(r);
                restZ = zIndexOf(cardElement);
                written = null;
                residualTop = 0;
            }
            lastPointer = pointer || lastPointer;
            handedOff = false;
            releaseOtherHolders();
            const vh = window.innerHeight;
            // Fit: the enlarged card must sit fully on screen with margins.
            let z = Math.min(maxScale, (vh - 2 * fitMargin) / restRect.height);
            if (z < 1) z = 1;
            // Clamp the enlarged box's top into the viewport.
            const maxTop = vh - fitMargin - restRect.height * z;
            fitTop = Math.min(Math.max(restRect.top, fitMargin), Math.max(maxTop, fitMargin));
            // Engrave the resting lines while the card is still exactly at
            // rest; they hold until clearZoom. A card caught mid-shrink is
            // still engraved from when it left rest.
            if (Math.abs(placed.z - 1) < 1e-3) pinDesc(cardElement);
            cardElement.style.position = 'relative';
            if (pose) {
                // The pose is inline from the first style recalc: the held
                // rule below says "upright", and with no transition to ease
                // it (`crisp-moving`) the card would stand up for one frame
                // before the first path frame put it back at 78 % of the fan.
                cardElement.style.setProperty('--card-base-transform', poseAt(pose, placed.pose));
                cardElement.classList.add('crisp-moving');
            }
            cardElement.classList.add(zoomClass);
            cardElement.classList.add('crisp-hovered');
            // The raster pipeline's cue that this card's display is consuming
            // its declared reserve, not being re-laid-out (cards.js).
            cardElement.dataset.cardRasterEnlarging = 'true';
            zoomed = true;
            destination = { z, top: aimTop() };
            stillFrames = 0;
            const start = performance.now();
            motion = {
                from: { ...placed },
                to: { z, left: 0, pose: 0 },
                start,
                duration: GROW_MS,
                grow: true,
                done: watchSettle,
            };
            slide = { from: placed.top, to: destination.top, start, duration: GROW_MS };
            if (!frame) frame = requestAnimationFrame(tick);
        }

        /* The pointer is tracked on the DOCUMENT while it holds the card. The
           card's own `mouseleave` cannot tell "the pointer moved off me" from
           "I moved out from under the pointer", and once the card has moved the
           pointer's later travel never touches it again — so the leave that
           matters is read from where the pointer actually goes. */
        let tracking = false;
        function setPointer(point) {
            pointer = point;
            if (point) lastPointer = point;
        }
        function pointerMovedTo(point) {
            // The resting slots decide. A pointer outside this card's own
            // resting shape that is over a neighbour's belongs to the
            // neighbour — at once, growing or grown — even though the
            // neighbour lies under this card's enlarged box. Elsewhere the
            // enlarged box is the card's to keep (reading down it).
            if (zoomed) {
                const owner = neighbourOwning(point);
                if (owner) {
                    handOff(owner, point);
                    return;
                }
            }
            if (moving()) {
                // Still arriving: record, keep the settle window open for a
                // pointer that keeps coming, and let the frame re-aim and judge
                // against the box the card is heading for.
                setPointer(point);
                watchSettle();
                return;
            }
            if (isOverCard(point) || (zoomed && climbsAbove(point))) {
                setPointer(point);
                // The lean now eases after this move (and a climb tightens the
                // slide): hold the box.
                if (zoomed) watchSettle();
                return;
            }
            realLeave();
        }
        function onDocumentMove(event) {
            if (!pointerInside || !pointer) return;
            if (!cardElement.isConnected) { untrack(); return; }
            const point = eventPoint(event);
            if (!point) return;
            // The browser replays a move at the SAME coordinates after a layout
            // change (its hover update). That is not the pointer moving, and
            // the card's own motion is never grounds for letting go.
            if (point.x === pointer.x && point.y === pointer.y) return;
            pointerMovedTo(point);
        }
        function onDocumentOut(event) {
            // relatedTarget null: the pointer left the window itself.
            if (!event.relatedTarget) realLeave();
        }
        function track() {
            if (tracking || !pointer) return;
            tracking = true;
            document.addEventListener('mousemove', onDocumentMove, true);
            document.addEventListener('mouseout', onDocumentOut, true);
        }
        function untrack() {
            if (!tracking) return;
            tracking = false;
            document.removeEventListener('mousemove', onDocumentMove, true);
            document.removeEventListener('mouseout', onDocumentOut, true);
        }

        function enter(point) {
            pointerInside = true;
            setPointer(point);
            track();
            const generation = ++hoverGeneration;
            const prepareRaster = Cards?.prepareCardRasterForInteraction;
            if (typeof prepareRaster !== 'function') {
                beginHover();
                return;
            }
            void Promise.resolve(prepareRaster(cardElement, 'hover')).then((ready) => {
                if (
                    ready !== true
                    || generation !== hoverGeneration
                    || !pointerInside
                    || !hoverElement.isConnected
                    || !cardElement.isConnected
                    || getBlockReason()
                ) return;
                beginHover();
            }).catch(() => {
                // Keep the already-visible resting paint when preparation
                // fails; never enlarge a card that could swap pixels mid-grow.
            });
        }

        function beginShrink(duration = SHRINK_MS) {
            if (pointerInside || !zoomed) return;
            // The hold ends now, with the shrink — so a surface rule bound
            // to it (the fan's upright card) returns together with the size.
            cardElement.classList.remove('crisp-hovered');
            if (pose) cardElement.classList.add('crisp-moving');
            // Still engraved from the grow: the shrink shows the resting lines.
            const start = performance.now();
            motion = {
                from: { ...placed },
                to: { z: 1, left: 0, pose: 1 },
                start,
                duration,
                grow: false,
                done: clearZoom,
            };
            slide = { from: placed.top, to: restRect.top, start, duration };
            if (!frame) frame = requestAnimationFrame(tick);
        }

        function realLeave({ immediate = false, handoff = false } = {}) {
            if (!pointerInside) return;
            pointerInside = false;
            pointer = null;
            hoverGeneration += 1;
            untrack();
            // Whatever the box was heading for, it is no longer heading there
            // (the lean keeps its last aim so the shrink can fade it out).
            destination = null;
            motion = null;
            slide = null;
            if (!zoomed) return;
            if (leaveFrame) cancelAnimationFrame(leaveFrame);
            if (immediate) {
                leaveFrame = 0;
                handedOff = handoff;
                beginShrink(handoff ? HANDOFF_SHRINK_MS : SHRINK_MS);
                return;
            }
            /* Do not shrink on the SAME frame the leave arrives: a re-entry on
               the next frame cancels it, so a pointer that only grazed the edge
               keeps the card grown. */
            leaveFrame = requestAnimationFrame(() => {
                leaveFrame = 0;
                beginShrink();
            });
        }

        engines.set(hoverElement, {
            enter,
            release: () => realLeave({ immediate: true, handoff: true }),
        });

        // Touch: tap enlarges, tap again acts. Callback mode drives the engine
        // directly, so a tap is a hover with no pointer to track, and the
        // dismissal is unconditional — a finger has no box to be inside.
        TouchPreview?.attach?.(hoverElement, {
            onPreview: () => {
                if (getBlockReason()) return;
                enter(null);
            },
            onDismiss: () => realLeave(),
        });
        hoverElement.addEventListener('mouseenter', (event) => {
            // TouchPreview owns tap inspection and its trailing
            // compatibility-mouse window.
            if (event?.isTrusted !== false && TouchPreview?.isSuppressingMouse?.()) return;
            if (getBlockReason()) return;
            const point = eventPoint(event);
            if (pointerInside && zoomed) {
                // The card came back under a pointer that never left it (its
                // own containment, or the browser's hover update): nothing to
                // start, nothing to restart.
                if (point) setPointer(point);
                return;
            }
            // Shrinking after handing the pointer to a neighbour: this box is
            // still large enough to be hit, but the pointer is in the
            // neighbour's slot. The neighbour hands back once this card is at
            // rest, if the pointer is still here.
            if (zoomed && handedOff) return;
            enter(point);
        });

        hoverElement.addEventListener('mouseleave', (event) => {
            const point = eventPoint(event);
            if (!point || !pointer) {
                // No pointer to reason about (synthetic leave, or a hover that
                // never had one): the classic unconditional leave.
                realLeave();
                return;
            }
            if (point.x === pointer.x && point.y === pointer.y) {
                // The pointer did not move; the card did. Not a leave. The
                // frames keep the box on its path under the pointer; at rest
                // the card never chases it, and the next real move decides.
                return;
            }
            pointerMovedTo(point);
        });
    }

    return {
        attach,
        pinDescription: pinDesc,
        unpinDescription: unpinDesc,
        /* The motion's clock, for surface rules that ride it. */
        timing: Object.freeze({ growMs: GROW_MS, shrinkMs: SHRINK_MS, ease: MOTION_EASE_CSS }),
    };
})();

export { CrispHover };
